import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "flowbite-react";
import { AiOutlineLoading } from "react-icons/ai";
import { NavBar, SideNav } from "../../components";
import Breadcrumb from "../../components/Breadcrumb";
import { fetchCoverage } from "../../redux/slices/crawler";

const SCOPES = [
  { value: "both", label: "Live + draft" },
  { value: "live", label: "Live only" },
  { value: "draft", label: "Draft only" },
];

function heatClass(count) {
  if (!count) return "bg-white text-gray-300";
  if (count === 1) return "bg-amber-50 text-amber-800";
  if (count <= 3) return "bg-amber-100 text-amber-900";
  if (count <= 6) return "bg-orange-200 text-orange-950";
  return "bg-red-200 text-red-950 font-semibold";
}

function keysFromRegistry(fields, limit) {
  const seen = new Set();
  const keys = [];
  for (const f of fields || []) {
    if (f?.key && !seen.has(f.key)) {
      seen.add(f.key);
      keys.push(f.key);
    }
  }
  return keys.slice(0, limit);
}

function keysFromMisses(schools, missKey, limit) {
  const seen = new Set();
  const keys = [];
  for (const school of schools || []) {
    for (const k of Object.keys(school[missKey] || {})) {
      if (!seen.has(k)) {
        seen.add(k);
        keys.push(k);
      }
    }
  }
  return keys.slice(0, limit);
}

const CatalogCoverage = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const scopeParam = searchParams.get("scope") || "both";
  const scope = SCOPES.some((s) => s.value === scopeParam)
    ? scopeParam
    : "both";

  const { coverage, coverageLoading, error } = useSelector(
    (state) => state.crawler
  );

  const [localScope, setLocalScope] = useState(scope);

  useEffect(() => {
    setLocalScope(scope);
  }, [scope]);

  useEffect(() => {
    dispatch(fetchCoverage({ limit: 20, scope }));
  }, [dispatch, scope]);

  const setScope = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === "both") params.delete("scope");
    else params.set("scope", next);
    setSearchParams(params, { replace: true });
  };

  const criticalKeys = useMemo(() => {
    const fromRegistry = keysFromRegistry(coverage.fields?.critical, 14);
    if (fromRegistry.length) return fromRegistry;
    return keysFromMisses(coverage.schools, "missingCritical", 14);
  }, [coverage]);

  const importantKeys = useMemo(() => {
    const fromRegistry = keysFromRegistry(coverage.fields?.important, 10);
    if (fromRegistry.length) return fromRegistry;
    return keysFromMisses(coverage.schools, "missingImportant", 10);
  }, [coverage]);

  const labelFor = (key) => {
    const all = [
      ...(coverage.fields?.critical || []),
      ...(coverage.fields?.important || []),
    ];
    const hit = all.find((f) => f.key === key);
    return hit?.label || key;
  };

  const reload = () =>
    dispatch(fetchCoverage({ limit: 20, scope: localScope }));

  return (
    <div className="antialiased bg-gray-50 dark:bg-gray-900 min-h-screen">
      <NavBar />
      <SideNav />
      <main className="p-4 md:ml-64 h-auto pt-20">
        <div className="sticky top-14 z-10 bg-gray-50 dark:bg-gray-900">
          <Breadcrumb />
        </div>

        <section className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
              Catalog coverage
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              School × field heatmap of missing critical and important
              registry fields (sampled from recent crawls and pending
              change sets).
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2 text-sm text-gray-600">
              Source
              <select
                className="rounded-lg border-gray-300 text-sm"
                value={localScope}
                disabled={coverageLoading}
                onChange={(e) => {
                  const next = e.target.value;
                  setLocalScope(next);
                  setScope(next);
                }}
              >
                {SCOPES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
            <Button
              color="purple"
              disabled={coverageLoading}
              onClick={reload}
            >
              Refresh
            </Button>
          </div>
        </section>

        {coverage.note && (
          <p className="text-xs text-gray-500 mb-4">{coverage.note}</p>
        )}

        {error && (
          <p className="text-sm text-red-600 mb-3">{String(error)}</p>
        )}

        {coverageLoading && !(coverage.schools || []).length ? (
          <div className="py-12 text-center">
            <AiOutlineLoading className="inline h-6 w-6 animate-spin text-purple-600" />
          </div>
        ) : !(coverage.schools || []).length ? (
          <p className="text-sm text-gray-500 py-8 text-center">
            No coverage data yet. Run a crawl or wait for pending change
            sets.
          </p>
        ) : (
          <>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              Missing critical
            </h3>
            <div className="overflow-x-auto bg-white rounded-lg border border-gray-200 mb-8">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 text-left text-gray-600">
                  <tr>
                    <th className="px-3 py-2 sticky left-0 bg-gray-50">
                      School
                    </th>
                    <th className="px-3 py-2">Pending</th>
                    <th className="px-3 py-2">Sample</th>
                    <th className="px-3 py-2">Avg cov.</th>
                    {criticalKeys.map((k) => (
                      <th
                        key={k}
                        className="px-2 py-2 font-normal max-w-[6rem] truncate"
                        title={labelFor(k)}
                      >
                        {labelFor(k)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(coverage.schools || []).map((row) => (
                    <tr
                      key={row.schoolId}
                      className="border-t border-gray-100"
                    >
                      <td className="px-3 py-2 sticky left-0 bg-white">
                        <Link
                          to={`/school/${row.schoolId}`}
                          className="font-medium text-purple-900 hover:underline"
                        >
                          {row.schoolName || row.schoolId}
                        </Link>
                        {row.needsAttention > 0 && (
                          <Link
                            to={`/review?tier=needs_attention`}
                            className="ml-2 text-[10px] text-amber-700 hover:underline"
                          >
                            {row.needsAttention} attn
                          </Link>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {row.pendingChangeSets}
                      </td>
                      <td
                        className="px-3 py-2 text-gray-600 whitespace-nowrap"
                        title={`Live ${row.sampleLive || 0} · Draft ${
                          row.sampleDraft || 0
                        }`}
                      >
                        {row.sampleSize || 0}
                        {(row.sampleLive || row.sampleDraft) && (
                          <span className="text-[10px] text-gray-400 ml-1">
                            ({row.sampleLive || 0}L/{row.sampleDraft || 0}D)
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {typeof row.avgCoverageAfter === "number"
                          ? `${Math.round(row.avgCoverageAfter * 100)}%`
                          : "—"}
                      </td>
                      {criticalKeys.map((k) => {
                        const n = row.missingCritical?.[k] || 0;
                        return (
                          <td
                            key={k}
                            className={`px-2 py-2 text-center ${heatClass(n)}`}
                          >
                            {n || "·"}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              Missing important
            </h3>
            <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 text-left text-gray-600">
                  <tr>
                    <th className="px-3 py-2 sticky left-0 bg-gray-50">
                      School
                    </th>
                    {importantKeys.map((k) => (
                      <th
                        key={k}
                        className="px-2 py-2 font-normal max-w-[6rem] truncate"
                        title={labelFor(k)}
                      >
                        {labelFor(k)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(coverage.schools || []).map((row) => (
                    <tr
                      key={`imp-${row.schoolId}`}
                      className="border-t border-gray-100"
                    >
                      <td className="px-3 py-2 sticky left-0 bg-white font-medium text-gray-800">
                        {row.schoolName || row.schoolId}
                      </td>
                      {importantKeys.map((k) => {
                        const n = row.missingImportant?.[k] || 0;
                        return (
                          <td
                            key={k}
                            className={`px-2 py-2 text-center ${heatClass(n)}`}
                          >
                            {n || "·"}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default CatalogCoverage;
